import { DataTableColumn } from 'mantine-datatable';
import { BaseRecord, CrudFilter, useList } from '@refinedev/core';
import { useEffect } from 'react';
import { TableData } from './useTable';
import { useClientPagination } from './useClientPagination';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

export const useClientPaginatedTable = <T extends BaseRecord>(
  resource: string,
  columns: DataTableColumn<T>[],
  searchTerm: string,
  filterPredicate?: (item: T, search: string) => boolean,
  filters?: CrudFilter[],
  pageSize: number = 10,
): TableData<T> => {
  const {
    data: rawData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useList<T>({
    resource,
    filters: filters || [],
  });

  useEffect(() => {
    if (isError && error) {
      notifyAndLogError(
        'Failed to load data',
        'An error occurred while loading table data. Please try again later.',
        error,
        { resource, filters, pageSize },
      );
    }
  }, [isError, error, resource, filters, pageSize]);

  const allData = rawData?.data || [];

  const { paginatedData, pagination, totalRecords, sortStatus, onSortStatusChange } =
    useClientPagination({
      data: allData,
      columns,
      searchTerm,
      filterPredicate,
      pageSize,
    });

  return {
    data: paginatedData,
    isLoading,
    isFetching,
    isError,
    error,
    pagination,
    totalRecords,
    refetch: () => {
      try {
        return refetch();
      } catch (error) {
        notifyAndLogError(
          'Refresh failed',
          'Failed to refresh table data. Please try again.',
          error,
          { resource },
        );
        throw error;
      }
    },
    sortStatus,
    onSortStatusChange,
  };
};
